import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//
// Config class
//
export class IntegrationConfig_MS_MFA extends IntegrationConfigBase {
  exemptUsers: ConfigExemptionInfo[];

  exemptGroups: ConfigExemptionInfo[];

  checkConditionalAccessPolicies: boolean;

  createChecklistItemsPerUser: boolean;

  constructor() {
    super();
    this.exemptUsers = [];
    this.exemptGroups = [];
    this.checkConditionalAccessPolicies = true;
    this.createChecklistItemsPerUser = false;
  }
}

//
// Evidence classes
//
export class UserMFAInfo {
  id: string = '';

  name: string = '';

  email: string = '';

  type: string = '';

  isAdmin: boolean = false;

  methodsRegistered: string[] = [];
}

export class ConfigExemptionInfo {
  id: string = '';

  name: string = '';

  email: string = '';
}

export class ConfigExemptionInfoDisplay extends ConfigExemptionInfo {
  type: 'user' | 'group' = 'user';

  constructor(init?: ConfigExemptionInfo) {
    super();
    this.id = init?.id || '';
    this.name = init?.name || '';
    this.email = init?.email || '';
  }
}

export enum MfaExemptionReason {
  None = 0,

  // Customer-configured exemptions
  ConfigExcludedUser = 10,
  ConfigExcludedGroupMember = 11,

  // Conditional Access-derived exemptions
  ConditionalAccessExcludedUser = 20,
  ConditionalAccessExcludedGroupMember = 21,
}

export class ExemptionInfo {
  isExempt: boolean = false;

  reason: MfaExemptionReason = MfaExemptionReason.None;

  userId: string = '';

  groupId: string = '';

  groupName: string = '';

  policyId: string = '';

  policyName: string = '';
}

export class UserMfaStatus {
  user: UserMFAInfo = new UserMFAInfo();

  exemption: ExemptionInfo = new ExemptionInfo();
}

export enum MfaConfigIssueReason {
  None = 0,
  PrunedInvalidUserId = 30,
  PrunedInvalidGroupId = 31,
  PrunedWrongObjectType = 32,
}

export class PrunedConfigItem {
  id: string = '';

  kind: string = ''; // "user" | "group"

  reason: MfaConfigIssueReason = MfaConfigIssueReason.None;

  message: string | undefined;
}

export class EntraMFAHandlerEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  totalUserCount: number = 0;

  totalReportUsersWithoutMfa: number = 0;

  totalFindingsAfterExemptions: number = 0;

  totalAccountsExempted: number = 0;

  prunedConfig: PrunedConfigItem[] = [];

  findings: UserMfaStatus[] = [];

  exemptions: UserMfaStatus[] = [];

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(`## ${t('integrations:MS_MFA.Report.SummaryHeading', { date: toLocaleDateTime(this.evaluatedAtUtc) })}`);
    sb.push(`- ${t('integrations:MS_MFA.Report.TotalUserCount', { count: this.totalUserCount })}`);
    sb.push(`- ${t('integrations:MS_MFA.Report.TotalReportedWithoutMfa', { count: this.totalReportUsersWithoutMfa })}`);
    sb.push(`- ${t('integrations:MS_MFA.Report.TotalAccountsExempted', { count: this.totalAccountsExempted })}`);
    sb.push(
      `- ${t('integrations:MS_MFA.Report.TotalFindingsAfterExemptions', { count: this.totalFindingsAfterExemptions })}`,
    );
    sb.push(`## ${t('integrations:MS_MFA.Report.FindingsHeading', { count: this.findings.length })}`);

    this.findings.forEach((f) => {
      const methods = f.user.methodsRegistered?.length
        ? f.user.methodsRegistered.join(', ')
        : t('integrations:MS_MFA.Report.MethodsNone');
      const adminLabel = f.user.isAdmin ? t('translation:General.Words.Yes') : t('translation:General.Words.No');

      sb.push(
        `- ${t('integrations:MS_MFA.Report.FindingLine', {
          name: f.user.name || '',
          email: f.user.email || '',
          id: f.user.id || '',
          admin: adminLabel,
          methods,
        })}`,
      );
    });

    return sb.join('\n');
  }
}
